import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  CalendarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import { firestore } from '../../database/firebase';
import { collection, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

const SECTIONS = [
  {
    id: 'businessFoundations',
    title: 'Business Foundations',
    items: [
      'Business registration and licenses obtained',
      'Business bank account opened',
      'Tax identification number (TIN) registered',
      'Business insurance policies in place',
      'Operating agreements and bylaws completed',
      'Business model',
      'Financial accounts and bookkeeping system set up',
    ],
  },
  {
    id: 'productReadiness',
    title: 'Product/Service Readiness',
    items: [
      'Product/service is production-ready',
      'Quality assurance testing completed',
      'Product documentation completed',
      'User manuals and guides created',
      'Support materials prepared',
      'Product pricing finalized',
      'Packaging and branding finalized',
      'Inventory or service capacity confirmed',
    ],
  },
  {
    id: 'gtmPreparation',
    title: 'Go-To-Market Preparation',
    items: [
      'GTM strategy documented and approved',
      'Marketing materials prepared',
      'Sales collateral created',
      'Pricing strategy finalized',
      'Distribution channels established',
      'Partnership agreements signed',
      'Launch timeline and milestones defined',
      'Marketing budget allocated',
    ],
  },
  {
    id: 'brandingDigital',
    title: 'Branding & Digital Presence',
    items: [
      'Brand identity and logo finalized',
      'Website launched and tested',
      'Social media accounts set up',
      'Email marketing system configured',
      'SEO and content strategy implemented',
      'Online presence optimized',
      'Brand guidelines documented',
      'Digital assets created and organized',
    ],
  },
  {
    id: 'marketingCampaign',
    title: 'Marketing Launch Campaign',
    items: [
      'Launch campaign strategy defined',
      'Marketing channels selected',
      'Content calendar created',
      'Advertising campaigns prepared',
      'PR and media outreach planned',
      'Influencer partnerships secured',
      'Launch event planned (if applicable)',
      'Campaign tracking and analytics set up',
      'Guestlist invitations - Has the guest list been developed',
      'Guestlist invitations - Who are you targeting',
      'Guestlist invitations - The relevant sectors',
    ],
  },
  {
    id: 'launchEventBranding',
    title: 'Launch Event Marketing & Branding Materials',
    items: [
      'Backdrop / Sublimated wall banner arranged for stage and photos',
      'Telescopic banners x2 prepared for venue entrance',
      'Pop-up banner x1 prepared for indoor branding and app feature highlights',
      'Branded T-shirts x20 for staff, MC, and brand ambassadors',
      'Caps x20 for staff and giveaways',
      'A5 flyers x1000 with app download QR codes and offers',
      'Business cards x150 for networking',
      'App demo stations/tablets set up for live demo and trial orders',
      'Large screen presentation prepared (looping video reel)',
      'QR code displays integrated on banners, flyers, and tables',
      'Videographer/photographer booked to capture event highlights',
      'Stickers and keyholders prepared as branded merchandise',
      'Branded water bottles or cups arranged',
    ],
  },
  {
    id: 'salesAcquisition',
    title: 'Sales & Customer Acquisition',
    items: [
      'Sales process defined and documented',
      'Sales team trained (if applicable)',
      'CRM system configured',
      'Lead generation process established',
      'Customer onboarding process defined',
      'Sales collateral and pitch decks ready',
      'Pricing and quoting tools prepared',
      'Customer acquisition targets set',
    ],
  },
  {
    id: 'operationsFinance',
    title: 'Operations & Finance',
    items: [
      'Operations processes documented',
      'Supply chain or service delivery established',
      'Inventory management systems in place',
      'Customer service processes defined',
      'Financial controls and reporting systems set up',
      'Payment processing configured',
      'Accounting and bookkeeping system operational',
      'Cash flow management plan in place',
    ],
  },
  {
    id: 'launchExecution',
    title: 'Launch Day Execution',
    items: [
      'Launch day checklist created',
      'Team roles and responsibilities assigned',
      'Customer support team ready',
      'Monitoring and tracking systems active',
      'Contingency plans prepared',
      'Launch announcement prepared',
      'Press release ready (if applicable)',
      'Launch day activities scheduled',
    ],
  },
];

const LaunchReadinessAnalytics = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'detailed'

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    const loadClientData = async () => {
      try {
        setLoading(true);

        // Get all clients
        const clientsSnapshot = await getDocs(collection(firestore, 'clients'));
        const clientsMap = {};
        clientsSnapshot.forEach(doc => {
          clientsMap[doc.id] = { id: doc.id, ...doc.data() };
        });

        // Get all launch checklist data
        const commercialisationSnapshot = await getDocs(collection(firestore, 'commercialisation'));
        const clientsData = [];

        for (const userDoc of commercialisationSnapshot.docs) {
          const userId = userDoc.id;
          const clientInfo = clientsMap[userId] || {};

          try {
            const launchDoc = await getDoc(
              doc(firestore, 'commercialisation', userId, 'launchChecklist', userId)
            );

            if (launchDoc.exists()) {
              const launchData = launchDoc.data();
              const responses = launchData.responses || {};

              // Calculate scores
              const sectionScores = {};
              let totalScore = 0;
              let totalItems = 0;

              SECTIONS.forEach(section => {
                let sectionScore = 0;
                section.items.forEach((_, index) => {
                  const value = responses[`${section.id}_${index}`];
                  if (value === 'yes') {
                    sectionScore += 1;
                  } else if (value === 'partial') {
                    sectionScore += 0.5;
                  }
                });
                const total = section.items.length;
                const percentage = total > 0 ? Math.round((sectionScore / total) * 100) : 0;
                sectionScores[section.id] = { checked: sectionScore, total, percentage };
                totalScore += sectionScore;
                totalItems += total;
              });

              const overallScore = totalItems > 0 ? Math.round((totalScore / totalItems) * 100) : 0;

              let readinessLevel = 'Not Ready';
              if (overallScore >= 85) {
                readinessLevel = 'Ready to Launch';
              } else if (overallScore >= 60) {
                readinessLevel = 'Almost Ready';
              }

              clientsData.push({
                id: userId,
                name:
                  clientInfo.firstname && clientInfo.lastname
                    ? `${clientInfo.firstname} ${clientInfo.lastname}`
                    : clientInfo.email || clientInfo.businessName || 'Unknown Client',
                email: clientInfo.email || '',
                businessName: clientInfo.businessName || '',
                score: overallScore,
                readinessLevel,
                totalChecked: totalScore,
                totalItems,
                sectionScores,
                lastUpdated: launchData.lastUpdated || launchData.updatedAt || launchData.timestamp,
              });
            }
          } catch (error) {
            console.error(`Error loading data for client ${userId}:`, error);
          }
        }

        setClients(clientsData.sort((a, b) => b.score - a.score));
      } catch (error) {
        console.error('Error loading launch readiness data:', error);
        toast.error('Failed to load launch readiness data', 'Error');
      } finally {
        setLoading(false);
      }
    };

    loadClientData();

    // Set up real-time listener
    const unsubscribe = onSnapshot(collection(firestore, 'commercialisation'), () => {
      loadClientData();
    });

    return () => unsubscribe();
  }, [currentUser?.uid, toast]);

  // Calculate overall statistics
  const stats = React.useMemo(() => {
    if (clients.length === 0) {
      return {
        total: 0,
        ready: 0,
        almostReady: 0,
        notReady: 0,
        averageScore: 0,
        categoryAverages: {},
        topGaps: [],
      };
    }

    const ready = clients.filter(c => c.readinessLevel === 'Ready to Launch').length;
    const almostReady = clients.filter(c => c.readinessLevel === 'Almost Ready').length;
    const notReady = clients.filter(c => c.readinessLevel === 'Not Ready').length;
    const averageScore = Math.round(clients.reduce((sum, c) => sum + c.score, 0) / clients.length);

    // Calculate category averages
    const categoryAverages = {};
    SECTIONS.forEach(section => {
      const scores = clients
        .map(c => c.sectionScores[section.id]?.percentage || 0)
        .filter(s => s > 0);
      if (scores.length > 0) {
        categoryAverages[section.id] = Math.round(
          scores.reduce((sum, s) => sum + s, 0) / scores.length
        );
      } else {
        categoryAverages[section.id] = 0;
      }
    });

    // Find top gaps (categories with lowest averages)
    const topGaps = Object.entries(categoryAverages)
      .map(([id, avg]) => ({
        id,
        title: SECTIONS.find(s => s.id === id)?.title || id,
        average: avg,
      }))
      .sort((a, b) => a.average - b.average)
      .slice(0, 5);

    return {
      total: clients.length,
      ready,
      almostReady,
      notReady,
      averageScore,
      categoryAverages,
      topGaps,
    };
  }, [clients]);

  const getReadinessColor = level => {
    switch (level) {
      case 'Ready to Launch':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Almost Ready':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-red-100 text-red-800 border-red-300';
    }
  };

  const getScoreColor = score => {
    if (score >= 85) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = score => {
    if (score >= 85) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
          <p className="text-sm text-gray-600">Loading launch readiness analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Launch Readiness Analytics</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Track client readiness for startup launch across all categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'overview' ? 'default' : 'outline'}
            onClick={() => setViewMode('overview')}
          >
            Overview
          </Button>
          <Button
            variant={viewMode === 'detailed' ? 'default' : 'outline'}
            onClick={() => setViewMode('detailed')}
          >
            Detailed View
          </Button>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Total Clients</p>
                <p className="mt-1 text-2xl font-bold text-text-primary">{stats.total}</p>
              </div>
              <UserGroupIcon className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Ready to Launch</p>
                <p className="mt-1 text-2xl font-bold text-green-600">{stats.ready}</p>
                <p className="text-xs text-text-secondary">
                  {stats.total > 0 ? Math.round((stats.ready / stats.total) * 100) : 0}% of clients
                </p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Almost Ready</p>
                <p className="mt-1 text-2xl font-bold text-yellow-600">{stats.almostReady}</p>
                <p className="text-xs text-text-secondary">
                  {stats.total > 0 ? Math.round((stats.almostReady / stats.total) * 100) : 0}% of
                  clients
                </p>
              </div>
              <ClockIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-secondary">Average Score</p>
                <p className={`mt-1 text-2xl font-bold ${getScoreColor(stats.averageScore)}`}>
                  {stats.averageScore}%
                </p>
                <p className="text-xs text-text-secondary">Across all clients</p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Category Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {SECTIONS.map(section => {
              const avg = stats.categoryAverages[section.id] || 0;
              return (
                <div key={section.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text-primary">{section.title}</span>
                    <span className={`text-sm font-semibold ${getScoreColor(avg)}`}>{avg}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${avg}%` }}
                      transition={{ duration: 0.5 }}
                      className={`h-full ${getScoreBgColor(avg)}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top Gaps */}
      {stats.topGaps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Areas Needing Attention</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topGaps.map((gap, index) => (
                <div
                  key={gap.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
                      {index + 1}
                    </div>
                    <span className="font-medium text-text-primary">{gap.title}</span>
                  </div>
                  <span className="text-sm font-semibold text-red-600">{gap.average}% avg</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client List */}
      <Card>
        <CardHeader>
          <CardTitle>Client Readiness Status</CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="py-12 text-center">
              <RocketLaunchIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm font-medium text-gray-600">
                No launch checklist data found
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Clients need to complete their launch checklist first
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map(client => (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`cursor-pointer rounded-lg border p-4 transition-all hover:shadow-md ${
                    selectedClient?.id === client.id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-white'
                  }`}
                  onClick={() =>
                    setSelectedClient(selectedClient?.id === client.id ? null : client)
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-text-primary">{client.name}</h3>
                        <Badge className={getReadinessColor(client.readinessLevel)}>
                          {client.readinessLevel}
                        </Badge>
                      </div>
                      {client.businessName && (
                        <p className="mt-1 text-xs text-text-secondary">{client.businessName}</p>
                      )}
                      <div className="mt-2 flex items-center gap-4 text-xs text-text-secondary">
                        <span>
                          {client.totalChecked}/{client.totalItems} items completed
                        </span>
                        {client.lastUpdated && (
                          <span>
                            Updated:{' '}
                            {new Date(client.lastUpdated.seconds * 1000).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getScoreColor(client.score)}`}>
                          {client.score}%
                        </p>
                        <div className="mt-1 h-2 w-24 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className={`h-full ${getScoreBgColor(client.score)}`}
                            style={{ width: `${client.score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedClient?.id === client.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 border-t pt-4"
                    >
                      <h4 className="mb-3 text-sm font-semibold text-text-primary">
                        Category Breakdown
                      </h4>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {SECTIONS.map(section => {
                          const sectionScore = client.sectionScores[section.id] || {
                            checked: 0,
                            total: section.items.length,
                            percentage: 0,
                          };
                          return (
                            <div
                              key={section.id}
                              className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-text-primary">
                                  {section.title}
                                </span>
                                <span
                                  className={`text-xs font-semibold ${getScoreColor(sectionScore.percentage)}`}
                                >
                                  {sectionScore.percentage}%
                                </span>
                              </div>
                              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                                <div
                                  className={`h-full ${getScoreBgColor(sectionScore.percentage)}`}
                                  style={{ width: `${sectionScore.percentage}%` }}
                                />
                              </div>
                              <p className="mt-1 text-xs text-text-secondary">
                                {sectionScore.checked}/{sectionScore.total} items
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LaunchReadinessAnalytics;
